import type { ReactNode } from "react";
import t from "@/lang/lang";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { FloatNumberInput } from "@/shared/components/domain/qpm/inputs/number-input";
import type { MonitorAdditionalReq } from "@/services/backend/qpm/monitors/types/monitor-commands";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import { normalizeMonitorAdditionalConfig } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import {
  EditableRentingFeeRow,
  EditableRentingFeesTable,
  sortedRentingFeeRows,
} from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/editable-renting-fees-table";
import { AdditionalConfigForm } from "@/routes/base-data/processes/qpm/_components/additional-config-form";

export type MonitorAdditionalEditValue = MonitorAdditionalReq;

export function MonitorAdditionalEditForm({
  additional,
  monitorType,
  disabled,
  readOnly,
  imageSlot,
  onChange,
}: {
  additional: MonitorAdditionalEditValue | undefined;
  monitorType: MonitorType;
  disabled: boolean;
  readOnly?: boolean;
  imageSlot?: ReactNode;
  onChange: (patch: Partial<MonitorAdditionalEditValue>) => void;
}) {
  if (!additional) {
    return (
      <div className="text-muted-foreground flex h-full min-h-72 items-center justify-center rounded-md border border-dashed p-6 text-sm">
        {t("Zusatz auswählen oder neu anlegen")}
      </div>
    );
  }

  const config = normalizeMonitorAdditionalConfig(additional.config);
  const controlDisabled = disabled || readOnly === true;
  const showImageSlot = config.showInOfferMonitorCatalog && imageSlot;

  return (
    <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div className="space-y-5">
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_160px]">
          <div className="space-y-2">
            <Label>{t("Name")}</Label>
            <Input
              value={additional.name}
              disabled={controlDisabled}
              onChange={(event) => onChange({ name: event.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>{t("Preis")}</Label>
            <FloatNumberInput
              className="h-8 w-36"
              value={additional.basePrice}
              fractions={2}
              disabled={controlDisabled}
              onChange={(basePrice) => onChange({ basePrice })}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t("Katalogtext / Beschreibung")}</Label>
          <Textarea
            value={additional.description}
            disabled={controlDisabled}
            onChange={(event) => onChange({ description: event.target.value })}
          />
        </div>

        <AdditionalConfigForm
          config={config}
          disabled={controlDisabled}
          onChange={(nextConfig) => onChange({ config: nextConfig })}
        />

        {monitorType === MonitorType.IoT ? (
          <div className="space-y-2">
            <div>
              <div className="font-medium">
                {t("Mietpreise für IoT Zusätze")}
              </div>
              <div className="text-muted-foreground text-sm">
                {t(
                  normalizedConfigRentingText(
                    config.includeInRentingCalculation,
                  ),
                )}
              </div>
            </div>
            <EditableRentingFeesTable
              rows={rentingFeesToRows(additional.rentingFees ?? {})}
              onChange={(rows) =>
                onChange({ rentingFees: rowsToRentingFees(rows) })
              }
              emptyText={t("Keine Mietpreise hinterlegt")}
              addButtonText={t("Mietpreis hinzufügen")}
              disabled={controlDisabled || !config.includeInRentingCalculation}
            />
          </div>
        ) : null}
      </div>

      {showImageSlot ? <div>{imageSlot}</div> : null}
    </div>
  );
}

function normalizedConfigRentingText(includeInRentingCalculation: boolean) {
  if (includeInRentingCalculation) {
    return "Diese Mietpreise werden als zusätzliche Mietposition berechnet.";
  }
  return "Aktivieren Sie Mietpreis verwenden, um diese Preise in der IoT-Mietkalkulation zu verwenden.";
}

export function rentingFeesToRows(
  rentingFees: Record<number, number>,
): EditableRentingFeeRow[] {
  return sortedRentingFeeRows(
    Object.entries(rentingFees)
      .map(([durationMonths, monthly]) => ({
        durationMonths: Number(durationMonths),
        monthly,
      }))
      .filter((fee) => fee.durationMonths > 0),
  );
}

export function rowsToRentingFees(
  rows: EditableRentingFeeRow[],
): Record<number, number> {
  return Object.fromEntries(
    sortedRentingFeeRows(rows)
      .filter((row) => row.durationMonths > 0)
      .map((row) => [row.durationMonths, row.monthly]),
  );
}
